/**
 * BusinessResultsTab — the entire Results tab from BusinessCalc, extracted
 * so the parent page stays under ~2800 lines.  Pure presentation + a bag of
 * callbacks and derived data supplied as props.
 *
 * Extraction moved ~470 lines out of `BusinessCalc.js`.  No behaviour change.
 */
import React from 'react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import {
  Sparkles, TrendingUp, Save, Download, ArrowRight, ChefHat,
  PackageOpen, Calculator, DollarSign, Package, Percent, Info,
  FileText, AlertCircle,
} from 'lucide-react';
import HeadlineMetric from '../HeadlineMetric';
import DataTable from '../DataTable';
import AdPlacement from './AdPlacement';
import ProFormaStatement from './ProFormaStatement';
import ProductionPlanning from './ProductionPlanning';
import MarketCompetition from './MarketCompetition';
import AIBusinessSummary from './AIBusinessSummary';

const BusinessResultsTab = ({
  // Data
  formData, outputs, sensitivity, competitionData, competitionLoading,
  optimizationResults, productNotes, quantityPerUnit, unitType,
  calculationMethod, piecesPerBag, totalBatchWeight, piecesPerTray, numTrays,
  showProductionPlanning, showMarketCompetition,
  // Demo-mode snapshot (skips paid AI call, shows pre-baked summary)
  isDemoPlan, aiBusinessSummary,
  // Callbacks
  exportPDF, calculateSensitivity, applyOptimization,
  setOptimizationResults, setShowProductionPlanning, setShowMarketCompetition,
  setShowSavePlanDialog, setProductNotes, setAiBusinessSummary,
  analyzeMarketCompetition,
  // Context
  API, navigate,
}) => {
  return (
 <div className="space-y-4">
 {/* Top Ad Placement */}
 <AdPlacement placement="results-top" />
 
 {outputs ? (
 <>
 {/* Professional Header for Print */}
 <div className="print-show mb-6">
 <div className="text-center border-b-4 border-blue-600 pb-4">
 <h1 className="text-3xl font-bold text-slate-900">BUSINESS FINANCIAL ANALYSIS</h1>
 <p className="text-lg text-slate-600 mt-2">{formData.plan_name || 'Business Plan'}</p>
 <p className="text-sm text-slate-500 mt-1">
 {formData.product_description && `Product: ${formData.product_description} • `}
 Prepared: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
 </p>
 </div>
 
 {/* Legal Disclaimer - Print Only */}
 <div className="mt-6 p-4 bg-amber-50 border-2 border-amber-400 rounded">
 <h3 className="font-bold text-sm text-amber-900 mb-2">⚠️ IMPORTANT DISCLAIMER</h3>
 <p className="text-xs text-amber-900 leading-relaxed">
 This financial analysis is provided for <strong>educational and exploratory purposes only</strong>. 
 All calculations, projections, and recommendations are hypothetical and based on user-provided inputs. 
 Freeze Dry Academy, Peak and Prairie Industries Corp., and their affiliates assume <strong>NO LIABILITY</strong> 
 for the accuracy, completeness, or suitability of these figures for any specific business purpose. Users bear full responsibility for independently 
 verifying all data, conducting their own research, and consulting with qualified financial, legal, 
 and business professionals before making any business decisions. This tool is for business exploration 
 and hypothetical testing only and does not constitute financial, investment, or business advice.
 </p>
 </div>
 </div>
 
 {/* On-Screen Disclaimer */}
 <Card className="border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/20 mb-4 no-print">
 <CardContent className="pt-4">
 <div className="flex gap-3">
 <div className="flex-shrink-0">
 <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold">
 !
 </div>
 </div>
 <div className="flex-1">
 <h3 className="font-bold text-amber-900 dark:text-amber-100 mb-2">Educational content advisory</h3>
 <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed mb-2">
 <strong>For Educational & Exploratory Purposes Only.</strong> This calculator provides hypothetical financial projections 
 based on your inputs. Freeze Dry Academy, Peak and Prairie Industries Corp., and their affiliates assume <strong>NO LIABILITY</strong> 
 for the accuracy or completeness of these calculations.
 </p>
 <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
 <strong>You are solely responsible</strong> for verifying all data, conducting independent research, and consulting 
 qualified professionals (accountants, financial advisors, attorneys) before making business decisions. 
 Not financial, investment, or business advice.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* ── Family B: Headline metric + CTA cards + DataTable ─────────── */}
 <div className="space-y-6 mb-6 no-print" data-testid="family-b-summary">
 <HeadlineMetric
 label={formData.plan_name || 'Business plan snapshot'}
 value={`$${parseFloat(outputs.monthly_profit || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
 valueSuffix="/ mo net"
 hint={parseFloat(outputs.monthly_profit) >= 0
 ? 'Projected monthly net profit at the batch cadence you entered.'
 : 'Projected monthly loss — adjust price, batch size or costs to move above break-even.'}
 sideMetrics={[
 { label: 'Net margin', value: `${outputs.net_margin_percent}%` },
 { label: 'Cost / unit', value: `$${outputs.cost_per_unit}` },
 { label: 'Break-even', value: `${outputs.breakeven_units} u` },
 { label: 'Payback', value: `${outputs.machine_payback_months} mo` },
 ]}
 testId="business-headline-metric"
 />

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <button
 type="button"
 onClick={() => setShowSavePlanDialog(true)}
 className="pro-card p-5 text-left flex items-center gap-4 hover:-translate-y-0.5 transition-transform"
 data-testid="family-b-save-cta"
 >
 <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
 <Save className="w-5 h-5" />
 </div>
 <div className="min-w-0">
 <div className="font-heading font-extrabold text-base">Save this plan</div>
 <div className="text-xs text-muted-foreground">Store it in your portal and revisit later.</div>
 </div>
 </button>
 <button
 type="button"
 onClick={exportPDF}
 className="pro-card p-5 text-left flex items-center gap-4 hover:-translate-y-0.5 transition-transform"
 data-testid="family-b-export-cta"
 >
 <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
 <Download className="w-5 h-5" />
 </div>
 <div className="min-w-0">
 <div className="font-heading font-extrabold text-base">Export to PDF</div>
 <div className="text-xs text-muted-foreground">Bank / partner-ready one-pager.</div>
 </div>
 </button>
 </div>

 <div>
 <div className="flex items-end justify-between mb-3">
 <div>
 <span className="eyebrow">Cost breakdown</span>
 <h2 className="font-heading text-xl font-bold tracking-tight mt-1">Per-batch line items</h2>
 </div>
 <span className="text-xs text-muted-foreground">Ring = share of total direct cost</span>
 </div>
 <DataTable
 testId="business-cost-table"
 rows={(() => {
 const direct = Math.max(1, parseFloat(outputs.total_direct_cost) || 1);
 const items = [
 { key: 'ingredients', label: 'Ingredients', icon: ChefHat, amount: parseFloat(formData.ingredient_cost) || 0 },
 { key: 'packaging',   label: 'Packaging',   icon: PackageOpen, amount: parseFloat(formData.packaging_cost) || 0 },
 { key: 'labor',       label: 'Labor',       icon: Calculator,  amount: parseFloat(formData.labor_cost) || 0 },
 { key: 'electricity', label: 'Electricity', icon: DollarSign,  amount: parseFloat(formData.electricity_cost) || 0 },
 { key: 'consumables', label: 'Consumables', icon: Package,     amount: parseFloat(formData.consumables_cost) || 0 },
 { key: 'overhead',    label: 'Overhead',    icon: Percent,     amount: parseFloat(outputs.overhead_per_batch) || 0 },
 ];
 return items
 .filter(i => i.amount > 0)
 .sort((a, b) => b.amount - a.amount)
 .map(i => ({
 id: i.key,
 icon: i.icon,
 label: i.label,
 sub: `${((i.amount / direct) * 100).toFixed(1)}% of direct cost`,
 value: `$${i.amount.toFixed(2)}`,
 progress: Math.min(100, (i.amount / direct) * 100),
 }));
 })()}
 emptyMessage="Enter your costs on the Inputs tab to see the breakdown here."
 />
 </div>
 </div>

 <ProFormaStatement formData={formData} outputs={outputs} exportPDF={exportPDF} />

 {/* Production Planning - Multi-Batch Revenue Projections */}
 <div className="production-planning-section">
 <ProductionPlanning
 showProductionPlanning={showProductionPlanning}
 setShowProductionPlanning={setShowProductionPlanning}
 formData={formData}
 cycleTimeHours={formData.cycle_time_hours}
 outputs={outputs}
 quantityPerUnit={quantityPerUnit}
 unitType={unitType}
 calculationMethod={calculationMethod}
 piecesPerBag={piecesPerBag}
 totalBatchWeight={totalBatchWeight}
 piecesPerTray={piecesPerTray}
 numTrays={numTrays}
 />
 </div>

 {/* AI Business Summary */}
 <AIBusinessSummary
 formData={formData}
 outputs={outputs}
 competitionData={competitionData}
 API={API}
 onSummaryGenerated={setAiBusinessSummary}
 isDemoPlan={isDemoPlan}
 existingSummary={aiBusinessSummary}
 />

 {/* Market Competition Analysis */}
 <MarketCompetition
 showMarketCompetition={showMarketCompetition}
 setShowMarketCompetition={setShowMarketCompetition}
 formData={formData}
 competitionData={competitionData}
 competitionLoading={competitionLoading}
 onUpdateAnalysis={analyzeMarketCompetition}
 outputs={outputs}
 marginStrategies={{
 margin25: outputs && outputs.cost_per_unit ? {
 requiredPrice: (parseFloat(outputs.cost_per_unit) / (1 - 0.25)).toFixed(2)
 } : null,
 margin45: outputs && outputs.cost_per_unit ? {
 requiredPrice: (parseFloat(outputs.cost_per_unit) / (1 - 0.45)).toFixed(2)
 } : null,
 margin65: outputs && outputs.cost_per_unit ? {
 requiredPrice: (parseFloat(outputs.cost_per_unit) / (1 - 0.65)).toFixed(2)
 } : null
 }}
 productionStats={{
 unitsPerMonth: parseInt(formData.units_per_batch || 0) * parseInt(formData.batches_per_month || 0),
 revenuePerMonth: parseFloat((parseInt(formData.units_per_batch || 0) * parseInt(formData.batches_per_month || 0) * parseFloat(formData.selling_price || 0)).toFixed(0))
 }}
 />

 <Button onClick={calculateSensitivity} variant="outline" className="w-full no-print">
 <TrendingUp className="w-4 h-4 mr-2" />
 Sensitivity Analysis
 </Button>

 {sensitivity && (
 <div className="print-sensitivity">
 <Card>
 <CardHeader>
 <CardTitle className="text-base">Sensitivity Analysis</CardTitle>
 <CardDescription className="text-xs">How changes affect net margin</CardDescription>
 </CardHeader>
 <CardContent className="space-y-2 text-sm">
 <div className="flex justify-between">
 <span>Base Margin:</span>
 <span className="font-bold">{sensitivity.base_margin}%</span>
 </div>
 <div className="flex justify-between">
 <span>Ingredient +10%:</span>
 <span className={sensitivity.ingredient_plus_10 < sensitivity.base_margin ? 'text-red-600' : 'text-green-600'}>
 {sensitivity.ingredient_plus_10}%
 </span>
 </div>
 <div className="flex justify-between">
 <span>Price +10%:</span>
 <span className="text-green-600">{sensitivity.price_plus_10}%</span>
 </div>
 <div className="flex justify-between">
 <span>Price -10%:</span>
 <span className="text-red-600">{sensitivity.price_minus_10}%</span>
 </div>
 <div className="flex justify-between">
 <span>Labor +20%:</span>
 <span className={sensitivity.labor_plus_20 < sensitivity.base_margin ? 'text-red-600' : ''}>
 {sensitivity.labor_plus_20}%
 </span>
 </div>
 </CardContent>
 </Card>
 </div>
 )}

 {optimizationResults && (
 <Card className="border-2 border-primary/30 bg-primary/5 dark:bg-navy/10">
 <CardHeader>
 <CardTitle className="text-base flex items-center gap-2">
 <Sparkles className="w-5 h-5 text-primary" />
 AI Optimization Recommendations
 </CardTitle>
 <CardDescription className="text-xs">{optimizationResults.explanation}</CardDescription>
 </CardHeader>
 <CardContent className="space-y-4">
 <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm">
 <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary dark:text-primary">
 <ArrowRight className="w-4 h-4" />
 Suggested Changes to Maximize Profit
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b-2 border-slate-200 dark:border-slate-700">
 <th className="text-left py-2 font-semibold">Field</th>
 <th className="text-center py-2 font-semibold">Current</th>
 <th className="text-center py-2 font-semibold">Optimized</th>
 <th className="text-right py-2 font-semibold">Change</th>
 </tr>
 </thead>
 <tbody>
 <tr className="border-t border-slate-100 dark:border-slate-800">
 <td className="py-3 font-medium">
 Units per Batch
 <div className="text-xs text-muted-foreground font-normal">Production output per cycle</div>
 </td>
 <td className="text-center text-slate-600 dark:text-slate-400">
 {optimizationResults.current.units_per_batch || '—'}
 </td>
 <td className="text-center font-bold text-primary">
 {optimizationResults.optimized.units_per_batch}
 </td>
 <td className="text-right">
 {optimizationResults.optimized.units_per_batch > (optimizationResults.current.units_per_batch || 0) ? (
 <span className="text-green-600 font-semibold flex items-center justify-end gap-1">
 <TrendingUp className="w-4 h-4" />
 +{(((optimizationResults.optimized.units_per_batch - optimizationResults.current.units_per_batch) / optimizationResults.current.units_per_batch) * 100).toFixed(0)}%
 </span>
 ) : (
 <span className="text-slate-500">—</span>
 )}
 </td>
 </tr>
 <tr className="border-t border-slate-100 dark:border-slate-800">
 <td className="py-3 font-medium">
 Batches per Month
 <div className="text-xs text-muted-foreground font-normal">Monthly production frequency</div>
 </td>
 <td className="text-center text-slate-600 dark:text-slate-400">
 {optimizationResults.current.batches_per_month || '—'}
 </td>
 <td className="text-center font-bold text-primary">
 {optimizationResults.optimized.batches_per_month}
 </td>
 <td className="text-right">
 {optimizationResults.optimized.batches_per_month > (optimizationResults.current.batches_per_month || 0) ? (
 <span className="text-green-600 font-semibold flex items-center justify-end gap-1">
 <TrendingUp className="w-4 h-4" />
 +{(((optimizationResults.optimized.batches_per_month - optimizationResults.current.batches_per_month) / optimizationResults.current.batches_per_month) * 100).toFixed(0)}%
 </span>
 ) : (
 <span className="text-slate-500">—</span>
 )}
 </td>
 </tr>
 <tr className="border-t border-slate-100 dark:border-slate-800">
 <td className="py-3 font-medium">
 Batches per Week
 <div className="text-xs text-muted-foreground font-normal">Weekly production rhythm</div>
 </td>
 <td className="text-center text-slate-600 dark:text-slate-400">
 {optimizationResults.current.batches_per_week || '—'}
 </td>
 <td className="text-center font-bold text-primary">
 {optimizationResults.optimized.batches_per_week}
 </td>
 <td className="text-right">
 {optimizationResults.optimized.batches_per_week > (optimizationResults.current.batches_per_week || 0) ? (
 <span className="text-green-600 font-semibold flex items-center justify-end gap-1">
 <TrendingUp className="w-4 h-4" />
 +{(((optimizationResults.optimized.batches_per_week - optimizationResults.current.batches_per_week) / optimizationResults.current.batches_per_week) * 100).toFixed(0)}%
 </span>
 ) : (
 <span className="text-slate-500">—</span>
 )}
 </td>
 </tr>
 </tbody>
 </table>
 </div>
 
 <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
 <div className="flex items-start gap-2">
 <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
 <p className="text-xs text-blue-900 dark:text-blue-100">
 <strong>Business Planning Context:</strong> These optimized values balance production capacity, overhead absorption, and market demand. Higher throughput reduces per-unit fixed costs, improving margins and breakeven timeline—key metrics for lenders and investors.
 </p>
 </div>
 </div>

 <div className="flex gap-2">
 <Button onClick={applyOptimization} className="flex-1 bg-primary hover:bg-primary/90" variant="default">
 <TrendingUp className="w-4 h-4 mr-2" />
 Apply These Values
 </Button>
 <Button onClick={() => setOptimizationResults(null)} variant="outline" className="flex-1">
 Dismiss
 </Button>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Product Research Notes Section */}
 {outputs && (
 <Card className="border-2 border-blue-200 dark:border-blue-800">
 <CardHeader>
 <CardTitle className="text-base flex items-center gap-2">
 <FileText className="w-5 h-5 text-primary" />
 Product Research Notes
 </CardTitle>
 <CardDescription className="text-xs">
 Document insights, observations, and details about your product analysis
 </CardDescription>
 </CardHeader>
 <CardContent>
 <textarea
 value={productNotes}
 onChange={(e) => setProductNotes(e.target.value)}
 placeholder="Add notes about your product research, market findings, ingredient sourcing, packaging decisions, target customers, marketing strategies, or any other relevant insights..."
 className="w-full min-h-[120px] p-3 border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 <p className="text-xs text-muted-foreground mt-2">
 💡 These notes will be saved with your plan for future reference
 </p>
 </CardContent>
 </Card>
 )}

 {/* Upgrade to Pro — HomeLyph Pro dark hero card */}
 {outputs && (
 <Card className="border-0 overflow-hidden">
 <div className="bg-navy-spotlight text-background p-8 sm:p-10 relative">
 <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
 <div className="max-w-xl">
 <span className="pill-badge"><Sparkles className="w-3.5 h-3.5" /> Tier One Pro</span>
 <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-4">
 Advanced calculators. AI recipe engine. Priority support.
 </h3>
 <p className="mt-2 text-background/70 text-sm max-w-md">
 Unlimited runs, deeper analytics, and access to the operator learning tracks.
 </p>
 </div>
 <Button
 size="lg"
 onClick={() => navigate('/pricing')}
 className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap"
 data-testid="upgrade-tier-one-btn"
 >
 See Pro features
 </Button>
 </div>
 </div>
 </Card>
 )}

 {/* Consulting Services Section */}
 {outputs && (
 <Card className="border-2 border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
 <CardContent className="py-6">
 <div className="space-y-4">
 <div className="flex items-start gap-4">
 <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
 <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
 </svg>
 </div>
 <div className="flex-1">
 <h3 className="text-lg font-bold text-amber-900 dark:text-amber-100 mb-2">
 Need Expert Guidance?
 </h3>
 <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
 Our freeze-drying business consultants can help you turn these calculations into a profitable reality. 
 Get personalized advice on equipment selection, product development, marketing strategies, and scaling your operations.
 </p>
 <div className="flex flex-wrap gap-2 text-xs text-amber-800 dark:text-amber-200 mb-4">
 <span className="flex items-center gap-1">
 ✓ Equipment Selection
 </span>
 <span className="flex items-center gap-1">
 ✓ Product Development
 </span>
 <span className="flex items-center gap-1">
 ✓ Market Strategy
 </span>
 <span className="flex items-center gap-1">
 ✓ Business Planning
 </span>
 </div>
 <Button
 variant="outline"
 className="bg-white dark:bg-slate-800 border-amber-600 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/30"
 onClick={() => window.open('https://freezedryacademy.com/consulting', '_blank')}
 >
 Learn About Consulting Services →
 </Button>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 )}
 </>
 ) : (
 <Card>
 <CardContent className="pt-6 text-center">
 <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
 <p className="text-muted-foreground">Fill in the form and click Calculate Results to see your financial projections</p>
 </CardContent>
 </Card>
 )}
 </div>

  );
};

export default BusinessResultsTab;
